import PropTypes from 'prop-types'
import { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { RETIRA_COMPRADOR } from '../../constants/mediosEnviosPagos.js'
import { ESTADO_PUBLICADA, mensajeConsultaWhatsapp } from '../../constants/publicaciones'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { armarLinkWhatsapp } from '../../helpers/whatsapp'
import { useAuth } from '../../hooks/useAuth'
import { resetModal, setModal } from '../../slices/modalSlice.js'
import { MediosEnvioChips, MediosPagoChips } from '../../ui/widgets/index.js'
import { CarritoContext } from '../context/carritoContext'
import { useDeshabilitaCompra } from '../hooks/deshabilitaCompra.js'
import { DetalleLayout } from '../reutilizables/detalleLayout/DetalleLayout.jsx'
import { GenericModal } from '../reutilizables/modal/GenericModal.jsx'
import Precios from '../reutilizables/precios/Precios'
import { SumarProductos } from '../reutilizables/sumarProductos/SumarProductos'
import * as listaEnvioService from '../servicios/listaEnvioService'
import * as mensajesService from '../servicios/mensajesService'
import * as presupuestosService from '../servicios/presupuestosService'
import LinkButton from '../ui/LinkButton'
import Carousel from './carousel/Carousel'
import styles from './DetalleCabecera.module.css'

const MAX_ESTRELLAS = 5
const MODAL_ID_PEDIR_PRESUPUESTO = 'pedir-presupuesto'
const MODAL_ID_PRESUPUESTO_INICIADO = 'presupuesto-iniciado'
const MODAL_ID_PEDIR_AGREGAR_CUENTA_CORRIENTE = 'pedir-cuenta-corriente'
const MODAL_ID_MENSAJE_ENVIADO = 'mensaje-enviado'
const ID_PERIODO_PRODUCCION_HORAS = 1
const ID_PERIODO_PRODUCCION_DIAS = 2
const ID_PERIODO_PRODUCCION_SEMANAS = 3
const ID_PERIODO_PRODUCCION_MESES = 4
const ID_PERIODO_PRODUCCION_AÑOS = 5
const MENSAJE_TIPO_PREGUNTA_COMPRADO = 1

const DetalleCabecera = ({
  detalle = {},
  esVentas,
  permiteComprar = true,
  calificacion = [],
  onScrollCalificacion,
  deshabilitaCompra = true,
  reglasAviso = {}
}) => {
  const {
    loginDataContext: { idUsuario, aceptaNoHayDatosFiscales, existeDatosFiscales, empid } = {},
    esPerfilAdmin,
    esAdminEBP
  } = useAuth()
  const [listaEnvios, setListaEnvios] = useState([])
  const { deshabilitaCompraPorPerfil, deshabilitaCompraPorLimite, mensajeLimite } =
    useDeshabilitaCompra(detalle)
  const dispatch = useDispatch()
  const pregunta = useSelector((state) => state.genericModal.values?.mensaje) ?? ''
  const noEsAdmin = !esPerfilAdmin && !esAdminEBP

  useEffect(() => {
    if (detalle.idVendedor) {
      listaEnvioService
        .buscarListaPorVendedor(detalle.idVendedor, detalle.idPublicacion)
        .then(({ data }) => setListaEnvios(data))
        .catch(() => setListaEnvios([]))
    }
  }, [detalle.idVendedor, detalle.idPublicacion])

  const mediosEnviosVigentes = (detalle.mediosEnvios || []).flatMap((medio) => {
    if (medio.idMedioEnvio !== RETIRA_COMPRADOR) return [medio]
    if (!medio.localidadRetiro || !medio.provinciaRetiro) return []

    const descZona = ` ${medio.localidadRetiro}, ${medio.provinciaRetiro}`
    return [{ ...medio, descZona }]
  })

  const porcDcto = Number(detalle.porcDctoVigente) || 0

  const {
    onAgregarCarrito,
    setVerCarrito,
    setIdPublicacionComprar,
    setIdPresupuestoComprar,
    setCantidadCompra,
    cantidadCompra,
    setPrecioUnitario,
    items
  } = useContext(CarritoContext)

  useEffect(() => {
    setCantidadCompra(detalle?.cantMinPedido || 1)
  }, [detalle.idPublicacion])

  useEffect(() => {
    let precioFinal = detalle.precioUnitario

    if (porcDcto > 0) {
      precioFinal = detalle.precioUnitario * (1 - porcDcto / 100)
    }

    setPrecioUnitario(precioFinal)
  }, [detalle.precioUnitario, porcDcto, setPrecioUnitario])

  const handleChangePregunta = (e) => {
    const { name, value } = e.target
    dispatch(setModal({ values: { [name]: value }, errores: {} }))
  }

  const abrirModalPresupuestoIniciado = () => {
    dispatch(
      setModal({
        show: true,
        size: 'sm',
        title: 'Presupuesto iniciado',
        type: 'jsx',
        modalId: MODAL_ID_PRESUPUESTO_INICIADO,
        body: ''
      })
    )
  }

  const presupuestoIniciadoContent = (
    <div>
      <p>
        Para consultarlo, podés ir directamente a la sección de{' '}
        <LinkButton
          className={styles.botonLink}
          to="/PresupuestosComprador"
          onClick={() => dispatch(resetModal())}
        >
          Presupuestos
        </LinkButton>
        .
      </p>
    </div>
  )

  const onSubmit = async () => {
    try {
      const presupuesto = {
        idUsuario,
        idPublicacion: parseInt(detalle.idPublicacion),
        primerMensaje:
          pregunta.trim() === ''
            ? `Se ha iniciado un presupuesto para la publicación ${detalle.titulo}`
            : pregunta
      }
      await presupuestosService.insertar(presupuesto)
      setTimeout(abrirModalPresupuestoIniciado, 0)
      return true
    } catch (error) {
      const mensaje = `Ocurrió un error al grabar el presupuesto.`
      toastErrorLog(error, mensaje, 'presupuestos')
      return false
    }
  }

  const agregarItemCarrito = async (idPub) => {
    setVerCarrito(true)
    let cantComprar = cantidadCompra
    if (items.length > 0) {
      const item = items.find((item) => item.idPublicacion === detalle.idPublicacion)

      if (item?.aPedido && item.cantidad + cantidadCompra > item.capacidadMax) {
        cantComprar = item.capacidadMax - item.cantidad
      }
    }
    const respuesta = await onAgregarCarrito(idPub, cantComprar)

    setCantidadCompra(detalle?.cantMinPedido || 1)

    if (respuesta) {
      toast.success('Producto agregado al carrito correctamente!', {
        toastId: 'accion-ok'
      })
    } else {
      toast.error('Hubo un error al agregar el producto', {
        toastId: 'accion-error'
      })
    }
  }

  const setIdPublicacionSel = (idPub, cantidad) => {
    setIdPresupuestoComprar(0)

    let precioFinal = detalle.precioUnitario

    if (detalle.preciosMayorista && detalle.preciosMayorista.length > 0) {
      const preciosMayoristasOrdenados = [...detalle.preciosMayorista].sort(
        (a, b) => b.cantidad - a.cantidad
      )
      const precioAplicable = preciosMayoristasOrdenados.find((p) => cantidad >= p.cantidad)

      if (precioAplicable) {
        precioFinal = precioAplicable.precio
      }
    }

    if (porcDcto > 0) {
      precioFinal = precioFinal * (1 - porcDcto / 100)
    }

    setPrecioUnitario(precioFinal)
    setIdPublicacionComprar(idPub)
  }

  const setCantidadElementos = (cantidad) => {
    const bounded = Math.max(
      detalle.cantMinPedido || 1,
      Math.min(cantidad, detalle.cantDisponible > 0 ? detalle.cantDisponible : detalle.capacidadMax)
    )
    setCantidadCompra(bounded)
  }
  const agregarAlCarrito = () => {
    if (deshabilitaCompraPorLimite) {
      toast.error(mensajeLimite)
      return
    }

    agregarItemCarrito(detalle.idPublicacion)
  }

  const incrementar = (e) => {
    e.stopPropagation()
    setCantidadCompra((prev) =>
      prev < detalle.cantDisponible || prev < detalle?.capacidadMax ? prev + 1 : prev
    )
  }

  const decrementar = (e) => {
    e.stopPropagation()
    setCantidadCompra((prev) =>
      prev > 1 && prev > detalle?.cantMinPedido ? prev - 1 : detalle?.cantMinPedido || 1
    )
  }

  const periodosProduccion = (id, tiempoProduccion) => {
    const plural = tiempoProduccion > 1 ? 's' : ''
    switch (id) {
      case ID_PERIODO_PRODUCCION_HORAS:
        return `hora${plural}`
      case ID_PERIODO_PRODUCCION_DIAS:
        return `día${plural}`
      case ID_PERIODO_PRODUCCION_SEMANAS:
        return `semana${plural}`
      case ID_PERIODO_PRODUCCION_MESES:
        return tiempoProduccion > 1 ? 'meses' : 'mes'
      case ID_PERIODO_PRODUCCION_AÑOS:
        return `año${plural}`
      default:
        return `día${plural}`
    }
  }

  const abrirModal = () => {
    dispatch(
      setModal({
        show: true,
        size: 'sm',
        title: 'Iniciar Presupuesto',
        type: 'form',
        modalId: MODAL_ID_PEDIR_PRESUPUESTO,
        editMode: true,
        errores: {},
        values: { mensaje: '' },
        body: 'Estás por iniciar un presupuesto con tu contraparte. \n Si aceptás ahora, podrán intercambiar mensajes y definir los detalles para una posible compra. \n Si estás seguro, podés enviarle un mensaje al vendedor:',
        campos: [
          {
            type: 'textarea',
            name: 'mensaje',
            label: 'Pregúntale algo al vendedor',
            isFullWidth: true,
            isEditable: true,
            isGrid: false,
            required: true
          }
        ]
      })
    )
  }

  const mensajeEnviadoContent = (
    <p>
      El mensaje fue enviado correctamente. Podrás ver el intercambio de mensajes privado en Compras
      →
      <LinkButton
        className={styles.botonLink}
        to="/PreguntasComprador"
        onClick={() => dispatch(resetModal())}
      >
        Preguntas
      </LinkButton>
      .
    </p>
  )

  const modalCuentaCorriente = () => {
    dispatch(
      setModal({
        show: true,
        size: 'sm',
        title: 'Solicitar cuenta corriente/datos vendedor',
        type: 'form',
        modalId: MODAL_ID_PEDIR_AGREGAR_CUENTA_CORRIENTE,
        editMode: true,
        errores: {},
        values: {
          mensaje: ''
        },
        campos: [
          {
            type: 'textarea',
            name: 'mensaje',
            label:
              'De acuerdo a tu condición, aquí podrás solicitar la habilitación de la cuenta corriente o los datos impositivos del vendedor para darlo de alta en tu maestro de proveedores.',
            isEditable: true,
            isGrid: false,
            required: true
          }
        ]
      })
    )
  }

  const onSubmitCuentaCorriente = async () => {
    if (pregunta.trim() === '') {
      dispatch(setModal({ errores: { mensaje: 'Este campo es obligatorio.' } }))
      return false
    }

    try {
      const cuentaCorrienteMensaje = {
        idComprador: idUsuario,
        idVendedor: detalle.idVendedor,
        idUsuarioChat: detalle.idVendedor,
        idUsuarioEmisor: idUsuario,
        idPublicacion: parseInt(detalle.idPublicacion),
        texto:
          pregunta.trim() === ''
            ? `Un usuario de la empresa ${empid} solicita habilitación de la cuenta corriente`
            : pregunta,
        privado: true,
        idMensajeTipo: MENSAJE_TIPO_PREGUNTA_COMPRADO
      }
      await mensajesService.insertar(cuentaCorrienteMensaje)
      setTimeout(() => {
        dispatch(
          setModal({
            show: true,
            size: 'sm',
            title: 'Mensaje enviado',
            type: 'jsx',
            modalId: MODAL_ID_MENSAJE_ENVIADO,
            body: ''
          })
        )
      }, 0)
      return true
    } catch (error) {
      const mensaje = `Ocurrió un error al grabar el Mensaje de Cuenta corriente.`
      toastErrorLog(error, mensaje, 'cuenta corriente')
      return false
    }
  }
  return (
    <DetalleLayout imagen={<Carousel detalle={detalle} />} titulo={detalle.titulo}>
      <div className={styles.calificacion}>
        {calificacion.length > 0 && (
          <button className={styles.estrellasBtn} onClick={onScrollCalificacion}>
            {[...Array(MAX_ESTRELLAS)].map((_, i) => (
              <i
                key={i}
                className={`fa fa-star ${i < Math.round(calificacion[0].promedioCalificacion) ? 'text-warning' : 'text-secondary'}`}
              />
            ))}
            <span className={styles.estrellasTexto}>({calificacion.length}) · Ver opiniones</span>
          </button>
        )}
      </div>
      {((detalle.idPedidoItem && detalle.vendedorNombre && !esVentas) ||
        detalle.publicacionEstado === null) && <h3>Vendedor: {detalle.vendedorNombre}</h3>}
      {detalle.cantidad && <p>Cantidad: {detalle.cantidad}</p>}
      {!reglasAviso.ocultarPrecioMayorista &&
      detalle.preciosMayorista &&
      detalle.preciosMayorista.length > 0 ? (
        <div className={styles.preciosMayoristaBloque}>
          <div className={styles.preciosMayoristaColumna}>
            <span className={styles.preciosMayoristaLabel}>Precio</span>
            <Precios
              precio={detalle.precioUnitario}
              monedaSimbolo={detalle.moneda.simbolo}
              alicuota={detalle.alicuota}
              showSinIVA={true}
              mostrarDescuento={porcDcto > 0}
              className={styles.precioCabecera}
              classNameDescuento={styles.precioCabeceraValorDescuento}
              classNameValor={styles.precioCabeceraValor}
              porcDcto={porcDcto}
            />
          </div>
          <div className={styles.preciosMayoristaColumna}>
            <span className={styles.preciosMayoristaLabel}>Precio mayorista</span>
            {detalle.preciosMayorista.map((r) => (
              <Precios
                key={r.idPublicacionPrecioMayorista}
                nombre={`+${r.cantidad} ${r.unidadMedida.nombre}`}
                precio={r.precioUnitario}
                monedaSimbolo={detalle.moneda.simbolo}
                alicuota={detalle.alicuota}
                showSinIVA={true}
                porcDcto={porcDcto}
                className={styles.precioMayorista}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.preciosMayoristaBloque}>
          <div className={`${styles.preciosMayoristaColumna}`}>
            <span className={styles.preciosMayoristaLabel}>Precio</span>
            {!detalle.conPresupuesto ? (
              <Precios
                precio={detalle.precioUnitario}
                monedaSimbolo={detalle?.moneda?.simbolo}
                alicuota={detalle.alicuota}
                showSinIVA={true}
                className={styles.precioCabecera}
                classNameDescuento={styles.precioCabeceraValorDescuento}
                classNameValor={styles.precioCabeceraValor}
                porcDcto={porcDcto}
              />
            ) : (
              <span className={styles.conPresupuestoLabel}>A cotizar con el vendedor</span>
            )}
          </div>
        </div>
      )}
      {reglasAviso.contactoPorWhatsapp && (
        <div className={styles.accionCompra}>
          <a
            className={`btn btn-success btn-comprar ${styles.btnComprar}`}
            href={armarLinkWhatsapp(detalle.vendedor, mensajeConsultaWhatsapp(detalle.titulo))}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fa fa-whatsapp mr-2" aria-hidden="true" />
            Contactar por WhatsApp
          </a>
        </div>
      )}
      {reglasAviso.permiteComprar &&
        detalle.idPublicacionEstado === ESTADO_PUBLICADA &&
        permiteComprar &&
        (detalle.conPresupuesto ? (
          <div className={styles.accionCompra}>
            <div className={styles.botonComprarWrapper}>
              <button
                className="btn-primary btnAccionCompra"
                disabled={!aceptaNoHayDatosFiscales && !existeDatosFiscales}
                onClick={abrirModal}
              >
                Pedir Presupuesto
              </button>
              {!aceptaNoHayDatosFiscales &&
                !existeDatosFiscales &&
                (!noEsAdmin ? (
                  <span className={styles.tooltipDatosFiscales}>
                    Completá tus datos fiscales para continuar.
                  </span>
                ) : (
                  <span className={styles.tooltipDatosFiscales}>
                    Por favor, comunicate con el administrador para que ingrese los datos fiscales o
                    acepte las condiciones.
                  </span>
                ))}
            </div>
          </div>
        ) : permiteComprar ? (
          <div>
            <MediosPagoChips
              mediosPagos={detalle.mediosPagos}
              onSolicitarCuentaCorriente={modalCuentaCorriente}
            />
            {!reglasAviso.ocultarMediosDeEnvio && (
              <MediosEnvioChips mediosEnvios={mediosEnviosVigentes} listaEnvios={listaEnvios} />
            )}
            {!deshabilitaCompraPorPerfil &&
              (detalle.cantDisponible > 0 || detalle.aPedido ? (
                <div className={styles.flexContainer}>
                  <SumarProductos
                    cantidad={cantidadCompra}
                    setCantidad={setCantidadElementos}
                    decrementar={decrementar}
                    incrementar={incrementar}
                    cantDisponible={
                      detalle.cantDisponible > 0 ? detalle.cantDisponible : detalle.capacidadMax
                    }
                    cantMin={detalle.cantMinPedido}
                    agregarAlCarrito={agregarAlCarrito}
                    deshabilitarBoton={deshabilitaCompra || deshabilitaCompraPorLimite}
                    aPedido={detalle.aPedido}
                  />

                  <div className={styles.accionCompra}>
                    <div className={styles.botonComprarWrapper}>
                      <LinkButton
                        className="btn-primary btnAccionCompra"
                        to={{ pathname: '/CompraDirecta', state: { detalle } }}
                        onClick={() => setIdPublicacionSel(detalle.idPublicacion, cantidadCompra)}
                        disabled={
                          deshabilitaCompra ||
                          deshabilitaCompraPorLimite ||
                          (!aceptaNoHayDatosFiscales && !existeDatosFiscales)
                        }
                      >
                        Comprar Ahora
                      </LinkButton>
                      {!aceptaNoHayDatosFiscales &&
                        !existeDatosFiscales &&
                        (!noEsAdmin ? (
                          <span className={styles.tooltipDatosFiscales}>
                            Completá tus datos fiscales para continuar.
                          </span>
                        ) : (
                          <span className={styles.tooltipDatosFiscales}>
                            Por favor, comunicate con el administrador para que ingrese los datos
                            fiscales o acepte las condiciones.
                          </span>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className={styles.sinStock}>SIN STOCK</p>
              ))}
            {!deshabilitaCompraPorPerfil && mensajeLimite && (
              <p className={styles.mensajeLimite}>{mensajeLimite}</p>
            )}
          </div>
        ) : null)}
      {detalle.aPedido && (
        <div className={styles.cantMinPedido}>
          <p>Cantidad mínima: {detalle.cantMinPedido}</p>
          <p>Cantidad máxima: {detalle.capacidadMax}</p>
          <p>
            Tiempo de producción: {detalle.cantTiempoProd}{' '}
            {periodosProduccion(detalle.periodosProduccion[0].id, detalle.cantTiempoProd)}
          </p>
          <p className={styles.leyendaTiempos}>
            Los tiempos de producción y de entrega son aproximados. Consultar con el vendedor para
            determinar/confirmar según la cantidad pedida.
          </p>
        </div>
      )}
      <GenericModal
        modalId={MODAL_ID_PEDIR_PRESUPUESTO}
        onChange={handleChangePregunta}
        onConfirm={onSubmit}
      />
      <GenericModal
        modalId={MODAL_ID_PRESUPUESTO_INICIADO}
        component={presupuestoIniciadoContent}
      />
      <GenericModal
        modalId={MODAL_ID_PEDIR_AGREGAR_CUENTA_CORRIENTE}
        onChange={handleChangePregunta}
        onConfirm={onSubmitCuentaCorriente}
      />
      <GenericModal modalId={MODAL_ID_MENSAJE_ENVIADO} component={mensajeEnviadoContent} />
    </DetalleLayout>
  )
}

export default DetalleCabecera

DetalleCabecera.propTypes = {
  calificacion: PropTypes.array,
  onScrollCalificacion: PropTypes.func,
  detalle: PropTypes.shape({
    idPublicacion: PropTypes.number.isRequired,
    titulo: PropTypes.string.isRequired,
    categorias: PropTypes.string,
    monedaSimbolo: PropTypes.string,
    precioUnitario: PropTypes.number,
    preciosMayorista: PropTypes.arrayOf(
      PropTypes.shape({
        idPublicacionPrecioMayorista: PropTypes.number.isRequired,
        cantidad: PropTypes.number,
        unidad: PropTypes.string,
        precio: PropTypes.number
      })
    ),
    conPresupuesto: PropTypes.bool,
    mediosPagos: PropTypes.array,
    mediosEnvios: PropTypes.array,
    cantDisponible: PropTypes.number.isRequired
  }).isRequired,
  esVentas: PropTypes.bool.isRequired,
  permiteComprar: PropTypes.bool,
  deshabilitaCompra: PropTypes.bool,
  reglasAviso: PropTypes.object
}
